/**
 * Contact Types for Frontend
 *
 * Mirrors backend types for type safety across the stack.
 */

// =============================================================================
// Contact Status
// =============================================================================

export type ContactStatus = 'active' | 'inactive' | 'blocked';

// =============================================================================
// Contact Location
// =============================================================================

export interface ContactLocation {
  state?: string; // UF (e.g., 'SP', 'RJ')
  city?: string;
  neighborhood?: string;
  street?: string;
  number?: string;
  complement?: string;
  zone?: string; // Zona eleitoral
  section?: string; // Seção eleitoral
  zipCode?: string;
}

// =============================================================================
// Contact Response (from API)
// =============================================================================

export interface Contact {
  id: string;
  tenantId: string;
  phone: string;
  name: string;
  email?: string;
  avatarUrl?: string;
  location: ContactLocation;
  tags: string[];
  customFields?: Record<string, string | number | boolean>;
  lastContactAt?: string;
  totalMessages: number;
  totalConversations: number;
  status: ContactStatus;
  optedOut: boolean;
  source?: string;
  origin?: string;
  isVip: boolean;
  birthday?: string; // ISO YYYY-MM-DD
  occupation?: string;
  addressHidden?: boolean; // true quando backend omitiu endereço (VIP sem permissão)
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Create/Update DTOs
// =============================================================================

export interface CreateContactInput {
  name: string;
  phone: string;
  email?: string | null;
  location?: Partial<ContactLocation>;
  tags?: string[];
  customFields?: Record<string, string | number | boolean>;
  isVip?: boolean;
  birthday?: string | null;
  occupation?: string | null;
  origin?: string | null;
}

export interface UpdateContactInput {
  name?: string;
  email?: string | null;
  location?: Partial<ContactLocation>;
  tags?: string[];
  customFields?: Record<string, string | number | boolean>;
  status?: ContactStatus;
  blockedReason?: string;
  optedOut?: boolean;
  isVip?: boolean;
  birthday?: string | null;
  occupation?: string | null;
  origin?: string | null;
}

// =============================================================================
// Import Types
// =============================================================================

export interface ImportContactRow {
  name: string;
  phone: string;
  email?: string;
  state?: string;
  city?: string;
  neighborhood?: string;
  street?: string;
  number?: string;
  complement?: string;
  tags?: string;
  isVip?: boolean;
  birthday?: string; // dd/mm/yyyy ou yyyy-mm-dd (backend normaliza)
  occupation?: string;
  origin?: string;
}

export type ImportDuplicateBehavior = 'skip' | 'merge';

export interface ImportResult {
  imported: number;
  updated: number;
  skipped: number;
  validationErrors: Array<{ row: number; error: string }>;
  errors: string[];
}

// =============================================================================
// List Response
// =============================================================================

export interface ContactListResponse {
  contacts: Contact[];
  nextCursor?: string;
  /** Quantidade nesta pagina (== contacts.length). */
  count: number;
  /** Total real de contatos do tenant (independente de filtros/paginacao). */
  total?: number;
}

export type ContactSortBy = 'default' | 'name' | 'city' | 'createdAt';
export type ContactSortDir = 'asc' | 'desc';

export interface ContactListParams {
  limit?: number;
  cursor?: string;
  status?: ContactStatus;
  city?: string;
  tag?: string;
  origin?: string;
  search?: string;
  sortBy?: ContactSortBy;
  sortDir?: ContactSortDir;
}

/**
 * Sugestões padrão de origem para popular o datalist mesmo antes do
 * tenant ter cadastrado qualquer contato. O campo aceita texto livre;
 * estes valores são apenas hints comuns em campanhas eleitorais.
 */
export const DEFAULT_ORIGIN_SUGGESTIONS = [
  'Evento presencial',
  'Comício / caminhada',
  'Porta a porta',
  'Indicação',
  'Redes sociais',
  'WhatsApp',
  'Site / landing page',
  'Formulário online',
  'Panfletagem',
  'Imprensa',
] as const;

// =============================================================================
// Public Registration (Santinho Digital)
// =============================================================================

export interface PublicContactInput {
  name: string;
  phone: string;
  city?: string;
  state?: string;
}

// =============================================================================
// Constants
// =============================================================================

export const CONTACT_STATUS_LABELS: Record<ContactStatus, string> = {
  active: 'Ativo',
  inactive: 'Inativo',
  blocked: 'Bloqueado',
};

export const CONTACT_STATUS_COLORS: Record<ContactStatus, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  blocked: 'bg-red-100 text-red-800',
};

export const BRAZILIAN_STATES = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
] as const;

// =============================================================================
// Validation Helpers
// =============================================================================

/**
 * Normalize Brazilian phone number to E.164 format (+5551991920606)
 *
 * Mobile numbers (local starts with 6-9) get the 9th digit inserted.
 * Landlines (local starts with 2-5) are kept as-is with 8 digits.
 */
export function normalizePhone(phone: string): string {
  let digits = phone.replace(/\D/g, '');

  if (digits.length === 10 || digits.length === 11) {
    digits = `55${digits}`;
  }

  if (digits.startsWith('55') && digits.length === 12) {
    const firstLocalDigit = digits.charAt(4);
    if ('6789'.includes(firstLocalDigit)) {
      const ddd = digits.slice(2, 4);
      const localNumber = digits.slice(4);
      digits = `55${ddd}9${localNumber}`;
    }
  }

  return `+${digits}`;
}

/**
 * Validate phone number format
 */
export function isValidPhone(phone: string): boolean {
  const normalized = normalizePhone(phone);
  return /^\+55\d{10,11}$/.test(normalized);
}

/**
 * Format phone for display: always normalizes first to guarantee
 * consistent output regardless of stored format.
 *
 * Mobile: any input → (51) 99289-0063
 * Landline: any input → (11) 4004-4828
 */
export function formatPhone(phone: string): string {
  const normalized = normalizePhone(phone).replace(/\D/g, '');

  // Mobile: 13 digits (55 + DDD + 9 + 8 digits)
  if (normalized.length === 13 && normalized.startsWith('55')) {
    const ddd = normalized.slice(2, 4);
    const part1 = normalized.slice(4, 9);
    const part2 = normalized.slice(9);
    return `(${ddd}) ${part1}-${part2}`;
  }

  // Landline: 12 digits (55 + DDD + 8 digits)
  if (normalized.length === 12 && normalized.startsWith('55')) {
    const ddd = normalized.slice(2, 4);
    const part1 = normalized.slice(4, 8);
    const part2 = normalized.slice(8);
    return `(${ddd}) ${part1}-${part2}`;
  }

  return phone;
}
